import { useMemo, useState, useEffect, useRef } from "react";
import Icon from "@mdi/react";
import { mdiPause, mdiPlay, mdiRestart, mdiTimer, mdiVibrate, mdiVibrateOff, mdiVolumeHigh, mdiVolumeLow, mdiVolumeOff } from "@mdi/js";

import { useRouter } from "easy-page-router/react";
import { useHead, useWakeLock } from "../../services/common";
import { useLang } from "../../services/lang";

import { Footer, Header } from "../../Layout";
import { Page, ShareButton } from "../../components";
import Dialog from "../../components/Dialog";

import { Time } from "./Countdown";


export default function TimerPage() {
    const { searchParams } = useRouter();
    const { dict } = useLang();

    const initialDuration = useMemo(() => {
        const s = parseInt(searchParams.s || "0", 10);
        const m = parseInt(searchParams.m || "0", 10);
        const h = parseInt(searchParams.h || "0", 10);

        return (h * 60 * 60 + m * 60 + s) * 1000;
    }, [searchParams]);

    const fakeMultiplier = parseFloat(searchParams.f || "1");

    const { hours, seconds, minutes } = useMemo(() => {
        let duration = initialDuration / 1000;
        if (fakeMultiplier !== 1) {
            duration *= fakeMultiplier;
        }

        const seconds = Math.floor(duration % 60);
        const minutes = Math.floor((duration / 60) % 60);
        const hours = Math.floor(duration / 3600);

        return { hours, minutes, seconds };
    }, [initialDuration, fakeMultiplier]);

    const titlePage = `${dict.timerTitle} ${hours ? `${hours}:${minutes.toString().padStart(2, '0')}:` : `${minutes}:`}${seconds.toString().padStart(2, '0')}`;

    const title = dict.seo.timer.getTitle(titlePage);
    const description = dict.seo.poll.getDescription(titlePage);

    useHead({
        title,
        description,
    });

    useWakeLock();

    const [timeLeft, setTimeLeft] = useState(initialDuration);
    const [isPaused, setIsPaused] = useState(true);

    const [showTimerDialog, setShowTimerDialog] = useState(false);

    const refAudio = useRef<HTMLAudioElement>(null);

    const [isAudioMuted, setIsAudioMuted] = useState(false);

    function playAudio() {
        if (!isAudioMuted && refAudio.current) {
            refAudio.current.play();
        }
    }

    const [isVibrateMuted, setIsVibrateMuted] = useState(false);
    const [isVibrateSupported, setIsVibrateSupported] = useState(false);

    useEffect(() => {
        if (typeof navigator !== "undefined" && 'vibrate' in navigator) {
            setIsVibrateSupported(true);
        }
    }, []);

    function vibrateDevice(duration: number[]) {
        if (!isVibrateMuted && typeof navigator !== "undefined" && 'vibrate' in navigator) {
            navigator.vibrate(duration);
        }
    }

    useEffect(() => {
        if (!isPaused && timeLeft > 0) {
            const ref = { current: true };
            const timeEnd = Date.now() + timeLeft;

            const tick = () => {
                requestAnimationFrame(() => {
                    if (ref.current) {
                        const timeLeft = timeEnd - Date.now();
                        if (timeLeft <= 0) {
                            playAudio();
                            vibrateDevice([250, 250, 250]);
                            setTimeLeft(0);
                        } else {
                            setTimeLeft(timeLeft);
                            tick();
                        }
                    }
                });
            }
            tick();

            return () => {
                ref.current = false;
            }
        }
    }, [isPaused, timeLeft]);

    const handlePlay = () => {
        if (timeLeft > 0) {
            setIsPaused(false);
        }
    };

    const handlePause = () => {
        setIsPaused(true);
    };

    const handleRestart = () => {
        setIsPaused(true);
        setTimeLeft(initialDuration);
        // setIsPaused(false); // run timer after restart?
    };

    let timeDisplay = timeLeft;
    if (timeLeft > 0 && fakeMultiplier !== 1) {
        // Fake timer
        const slowDownFactor = fakeMultiplier; // fakeMultiplier > 1 ? 1.5 : fakeMultiplier < 1 ? 0.5 : 1;
        const realProgress = timeLeft / initialDuration;
        const displayedProgressFactor = Math.pow(realProgress, 1 / slowDownFactor);
        timeDisplay = initialDuration * fakeMultiplier * displayedProgressFactor;
    }

    return <Page fixed={<>
        <Header title={dict.countdown} link={`/${dict.urlCountdown}`}>
            <a onClick={() => setShowTimerDialog(true)}>
                <Icon path={mdiTimer} size={1} />
            </a>
            <div />
            <a onClick={() => setIsAudioMuted(!isAudioMuted)}>
                <Icon path={isAudioMuted ? mdiVolumeOff : mdiVolumeHigh} size={1} />
            </a>
            {isVibrateSupported && <a onClick={() => setIsVibrateMuted(!isVibrateMuted)}>
                <Icon path={isVibrateMuted ? mdiVibrateOff : mdiVibrate} size={1} />
            </a>}
            <div />
            <ShareButton title={title} description={description} />
        </Header>
        <Footer />
    </>}>
        <div className={`full-page countdown timer`}>
            <div className="clickable" onClick={() => setShowTimerDialog(true)}>
                <h1>{titlePage}</h1>
                <Time diffTime={timeDisplay} />
            </div>
            {timeLeft <= 0 && <h3 className="message-after" style={{ marginTop: '20px' }}>{dict.timerEndMessage}</h3>}

            <div className="timer-controls" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button onClick={handlePlay} disabled={!isPaused || timeLeft <= 0}>
                    <Icon path={mdiPlay} size={2} />
                </button>
                <button onClick={handlePause} disabled={isPaused || timeLeft <= 0}>
                    <Icon path={mdiPause} size={2} />
                </button>
                <button onClick={handleRestart}>
                    <Icon path={mdiRestart} size={2} />
                </button>
            </div>
        </div>

        <audio className="hidden" ref={refAudio} >
            <source src="/timer.mp3" type="audio/mpeg" />
        </audio>

        <TimerDialog
            show={showTimerDialog}
            defaultTime={{ minutes, seconds }}
            onClose={() => setShowTimerDialog(false)}
        />
    </Page>;
}

export type TimerSettings = {
    minutes: number,
    seconds: number,
};

export function TimerDialog({ show, defaultTime, onClose }: { show: boolean, defaultTime: TimerSettings, onClose: () => void }) {
    const { dict } = useLang();
    const { push } = useRouter();

    const ref = useRef<HTMLInputElement>(null);

    const [timer, setTimer] = useState<TimerSettings>(defaultTime);
    const [focus, setFocus] = useState<null | number>(null);

    function handleFocus(position: number) {
        if (show && ref.current) {
            setFocus(position);
            ref.current.focus();
        }
    }
    function handleBlur() {
        if (ref.current) {
            setFocus(null);
            ref.current.blur();
        }
    }

    function handleChange(key: string) {
        if (focus === null) return;

        if (key === "Enter") {
            handleSubmit();
        }

        if (key === "Backspace") {
            const numbers = (timer.minutes.toString().padStart(2, '0') + timer.seconds.toString().padStart(2, '0')).split("");
            numbers[focus] = "0";

            const minutes = parseInt(numbers.slice(0, 2).join(""), 10);
            const seconds = parseInt(numbers.slice(2, 4).join(""), 10);

            setTimer({ minutes, seconds });
            setFocus((focus - 1 + 4) % 4);
        }
        if (key === "ArrowLeft") {
            setFocus((focus - 1 + 4) % 4);
        }
        if (key === "ArrowRight") {
            setFocus((focus + 1) % 4);
        }

        // is key number
        if (!isNaN(parseInt(key, 10))) {
            const numbers = (timer.minutes.toString().padStart(2, '0') + timer.seconds.toString().padStart(2, '0')).split("");
            numbers[focus] = key;

            const minutes = parseInt(numbers.slice(0, 2).join(""), 10);
            const seconds = parseInt(numbers.slice(2, 4).join(""), 10);

            setTimer({ minutes, seconds });
            setFocus((focus + 1) % 4);
        }
    }

    function handleSubmit() {
        push(`/${dict.urlTimer}?m=${timer.minutes}&s=${timer.seconds}`);
        onClose();
    }

    const minutes = timer.minutes.toString().padStart(2, '0');
    const seconds = timer.seconds.toString().padStart(2, '0');

    useEffect(() => {
        if (show && ref.current) {
            handleFocus(0);
        }
    }, [show]);

    return <Dialog show={show} onClose={onClose}>
        <h2>{dict.timerSettingsTitle}</h2>

        <div className="block timer-input">
            <span className={focus === 0 ? "focus" : ""} onClick={() => handleFocus(0)}>{minutes.charAt(0)}</span>
            <span className={focus === 1 ? "focus" : ""} onClick={() => handleFocus(1)}>{minutes.charAt(1)}</span>
            <span className="separator">:</span>
            <span className={focus === 2 ? "focus" : ""} onClick={() => handleFocus(2)}>{seconds.charAt(0)}</span>
            <span className={focus === 3 ? "focus" : ""} onClick={() => handleFocus(3)}>{seconds.charAt(1)}</span>
        </div>
        <input
            ref={ref}
            // className="hidden"
            style={{ position: "absolute", top: -10000, left: -10000, opacity: 0, pointerEvents: 'none' }}
            type="number"
            onKeyDown={e => handleChange(e.key)}
            // onFocus={() => handleFocus(0)}
            onBlur={handleBlur}
        />
        <button className="button" onClick={handleSubmit}>{dict.timerSettingsButton}</button>

    </Dialog >;
}
